import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JOB_ACTIVITY_KINDS,
  localWallTimeUtc,
  type CommercialJobActivityCreateInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { useOrgClock } from '../../lib/useOrgClock';
import { useOnline } from '../ui/useConnection';
import { Field, field } from '../create/Field';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SearchInput } from '../ui/SearchInput';
import { useServerTable } from '../ui/useServerTable';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconJobs } from '../icons';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from './CommercialWriteRecovery';

/** The feed displays server-authored events; logging an activity does not send anything. */
export function JobActivityFeed({ commercialJobId }: { commercialJobId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const online = useOnline();
  const clock = useOrgClock();
  const [filter, setFilter] = useState<CommercialJobActivityCreateInput['kind'] | ''>('');
  const { search, setSearch, query, table } = useServerTable(25, filter);
  const q = useQuery(
    trpc.commercialJob.activity.queryOptions({
      ...query,
      commercialJobId,
      kind: filter || undefined,
    }),
  );
  const members = useQuery(trpc.org.members.queryOptions());
  const [composing, setComposing] = useState(false);
  const [kind, setKind] = useState<CommercialJobActivityCreateInput['kind']>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const recovery = useCommercialWriteRecovery(
    'commercialJob.addActivity',
    commercialJobId,
  );
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: trpc.commercialJob.activity.pathKey() });
  const add = useMutation(
    trpc.commercialJob.addActivity.mutationOptions({
      onSuccess: async (_result, request) => {
        if (!(await recovery.complete(request))) return;
        setComposing(false);
        setTitle('');
        setBody('');
        setOccurredAt('');
        setDueAt('');
        setAssignedUserId('');
        refresh();
      },
    }),
  );
  const complete = useMutation(
    trpc.commercialJob.completeActivity.mutationOptions({ onSuccess: refresh }),
  );
  const state = shownState(q, q.data);
  const localDate = (value: string) => {
    if (!value || !clock.tz) return undefined;
    const [date, time] = value.split('T');
    const [h, m] = time!.split(':').map(Number);
    return localWallTimeUtc(date!, h! * 60 + m!, clock.tz);
  };
  const timeLabel = (value: string | Date) =>
    clock.tz
      ? new Date(value).toLocaleString(undefined, {
          timeZone: clock.tz,
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : 'Time unavailable until the shop timezone loads';
  const sendActivity = async () => {
    if (!online || add.isPending || recovery.preparing) return;
    const request =
      recovery.read.status === 'ready'
        ? recovery.read.request
        : {
            commercialJobId,
            kind,
            title: title.trim(),
            body: body || undefined,
            occurredAt: localDate(occurredAt),
            dueAt: kind === 'task' ? localDate(dueAt) : undefined,
            assignedUserId: assignedUserId || undefined,
            ...idem(),
          };
    if (await recovery.prepare(request)) add.mutate(request);
  };
  const blocked = add.isPending || recovery.preparing || recovery.read.status !== 'empty';
  return (
    <SectionCard
      title="Activity"
      right={
        <Button
          size="sm"
          disabled={add.isPending}
          onClick={() => setComposing((value) => !value)}
        >
          Log activity
        </Button>
      }
    >
      <div className="space-y-4">
        <CommercialWriteRecovery
          read={recovery.read}
          error={recovery.error}
          pending={add.isPending || recovery.preparing}
          online={online}
          onRetry={() => void sendActivity()}
          onResolve={() =>
            void recovery.resolveAttempt().then((resolved) => {
              if (resolved) add.reset();
            })
          }
        />
        {composing ? (
          <form
            className="space-y-3 rounded-[var(--radius)] border border-border p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (
                !online ||
                !title.trim() ||
                blocked ||
                ((occurredAt || dueAt) && !clock.tz)
              )
                return;
              void sendActivity();
            }}
          >
            <fieldset disabled={blocked} className="space-y-3">
              <Field label="Activity type">
                <select
                  className={field}
                  value={kind}
                  onChange={(event) => setKind(event.target.value as typeof kind)}
                >
                  {JOB_ACTIVITY_KINDS.map((value) => (
                    <option key={value} value={value}>
                      {value[0]!.toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Title">
                <input
                  className={field}
                  maxLength={200}
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </Field>
              <Field label="Details">
                <textarea
                  className={`${field} min-h-24 py-2`}
                  maxLength={10000}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={`Occurred at · ${clock.tz ?? 'timezone loading'}`}>
                  <input
                    className={field}
                    type="datetime-local"
                    disabled={!clock.tz}
                    value={occurredAt}
                    onChange={(event) => setOccurredAt(event.target.value)}
                  />
                </Field>
                <Field label="Assigned to">
                  <select
                    className={field}
                    value={assignedUserId}
                    onChange={(event) => setAssignedUserId(event.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {(members.data ?? []).map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {kind === 'task' ? (
                <Field label={`Task due · ${clock.tz ?? 'timezone loading'}`}>
                  <input
                    className={field}
                    type="datetime-local"
                    disabled={!clock.tz}
                    value={dueAt}
                    onChange={(event) => setDueAt(event.target.value)}
                  />
                </Field>
              ) : null}
              <p className="text-xs text-muted-foreground">
                An empty occurrence time records now. A task's due date is separate from
                its completion time. This entry sends no email or reminder.
              </p>
            </fieldset>
            {add.error ? (
              <Notice tone="danger" alert>
                {add.error.message}
              </Notice>
            ) : null}
            {!online ? (
              <Notice tone="warn">
                Reconnect to log this activity. Your draft remains here.
              </Notice>
            ) : null}
            <Button type="submit" disabled={!online || blocked || !title.trim()}>
              {add.isPending ? 'Saving…' : 'Save activity'}
            </Button>
          </form>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search activities"
          />
          <select
            className={`${field} max-w-40`}
            aria-label="Activity type filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="">All activities</option>
            {JOB_ACTIVITY_KINDS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <StaleNotice
          show={refreshFailed(q, q.data) || q.isPaused}
          onRetry={() => void q.refetch()}
        />
        {complete.error ? (
          <Notice tone="danger" alert>
            {complete.error.message}
          </Notice>
        ) : null}
        {state !== 'ready' ? (
          <UnresolvedState
            state={state}
            icon={IconJobs}
            what="this Job's activity"
            error={q.error}
            onRetry={() => void q.refetch()}
          />
        ) : q.data ? (
          <>
            <ol className="space-y-3">
              {q.data.rows.map((activity) => (
                <li
                  key={activity.id}
                  className="rounded-[var(--radius)] border border-border bg-surface p-3"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <StatusBadge tone={activity.source === 'manual' ? 'neutral' : 'info'}>
                      {activity.source === 'manual' ? activity.kind : 'Automatic'}
                    </StatusBadge>
                    <time dateTime={new Date(activity.occurredAt).toISOString()}>
                      {timeLabel(activity.occurredAt)}
                    </time>
                  </div>
                  <p className="text-sm font-semibold">{activity.title}</p>
                  {activity.body ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm">{activity.body}</p>
                  ) : null}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {activity.actorName ||
                      (activity.source === 'automatic' ? 'System' : 'Member unavailable')}
                  </p>
                  {activity.dueAt ? (
                    <p className="mt-2 text-xs">Due: {timeLabel(activity.dueAt)}</p>
                  ) : null}
                  {activity.completedAt ? (
                    <p className="mt-2 text-xs">
                      Completed: {timeLabel(activity.completedAt)}
                    </p>
                  ) : activity.source === 'manual' &&
                    activity.kind === 'task' &&
                    activity.activityId &&
                    activity.version ? (
                    <Button
                      className="mt-2"
                      variant="ghost"
                      size="sm"
                      disabled={!online || complete.isPending}
                      onClick={() =>
                        complete.mutate({
                          activityId: activity.activityId!,
                          expectedVersion: activity.version!,
                          ...idem(),
                        })
                      }
                    >
                      Mark task complete
                    </Button>
                  ) : null}
                  {activity.recordType === 'quote' ? (
                    <Link
                      className="mt-2 block text-xs font-semibold hover:underline"
                      to="/quotes/$quoteId"
                      params={{ quoteId: activity.recordId }}
                    >
                      Open quote
                    </Link>
                  ) : activity.recordType === 'job' ? (
                    <Link
                      className="mt-2 block text-xs font-semibold hover:underline"
                      to="/work-orders/$workOrderId"
                      params={{ workOrderId: activity.recordId }}
                    >
                      Open work order
                    </Link>
                  ) : null}
                </li>
              ))}
            </ol>
            {q.data.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No matching activity yet.</p>
            ) : null}
            <div className="flex items-center justify-between gap-3 text-xs">
              <Button
                variant="ghost"
                size="sm"
                disabled={query.offset === 0 || q.isFetching}
                onClick={() => table(q.data.total).onPage(query.offset / query.limit - 1)}
              >
                Previous
              </Button>
              <span>{q.data.total} activities</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={query.offset + query.limit >= q.data.total || q.isFetching}
                onClick={() => table(q.data.total).onPage(query.offset / query.limit + 1)}
              >
                Next
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </SectionCard>
  );
}
